import React from 'react';

import { useVisualAssetState, VisualAsset, type VisualAssetProps, type VisualAssetStateOptions } from '@pkg/components';

import { StyledThumbnail } from './styles';

// TODO: Ideally, `bypassLoading` is replaced by an
// ability to consult a "cache table" upon mount.
export interface ThumbnailProps
    extends Pick<VisualAssetProps, 'id' | 'title' | 'videoUrl' | 'imageUrl' | 'autoPlay' | 'controls'> {
    bypassLoading?: boolean;
    isLoading?: boolean;
    mashWidth?: VisualAssetStateOptions['mashWidth'];
    onError?: VisualAssetStateOptions['onError'];
    onLoad?: VisualAssetStateOptions['onLoad'];
}

export function Thumbnail({
    id,
    title,
    isLoading = false,
    bypassLoading = false,
    autoPlay = false,
    controls = false,
    ...stateProps
}: ThumbnailProps) {
    const {
        mashedVideo,
        mashedImage,
        errorHandler,
        loadedHandler,
        showError,
        showLoading,
        // A broken `poster` passed to a `<video />` will not throw
        // an error. We utilize `videoError` so that we don't assume
        // a `poster` fallback and can instead fallback to the
        // `<img />`, which itself can then throw an error.
        videoError,
    } = useVisualAssetState({ ...stateProps });

    const errorMarkup = showError ? <VisualAsset.Fallback key={`Thumbnail-Fallback-${id}`} /> : null;

    const _showLoading = isLoading || (!bypassLoading && showLoading);
    const skeletonMarkup = _showLoading ? <VisualAsset.Skeleton key={`Thumbnail-Skeleton-${id}`} /> : null;

    return (
        <StyledThumbnail>
            <VisualAsset
                id={id}
                title={title}
                videoUrl={videoError ? undefined : mashedVideo}
                imageUrl={mashedImage}
                autoPlay={autoPlay}
                controls={controls}
                hideAsset={(showError || showLoading) && !bypassLoading}
                onError={errorHandler}
                onLoad={loadedHandler}
            >
                {errorMarkup}
                {skeletonMarkup}
            </VisualAsset>
        </StyledThumbnail>
    );
}
